/**
 * 서비스워커 등록 — 홈 화면 설치와 웹푸시의 전제 조건.
 *
 * 왜 파일만 두면 안 되나
 *   `public/sw.js` 가 서빙되는 것만으로는 아무 일도 일어나지 않는다. 안드로이드 Chrome 의
 *   설치 가능 판정은 "매니페스트 + fetch 핸들러가 있는 **등록된** 서비스워커" 를 요구하고,
 *   웹푸시의 `pushManager.subscribe()` 는 `ServiceWorkerRegistration` 의 메서드라
 *   등록이 없으면 호출할 방법 자체가 없다.
 *
 * 이 파일이 하는 일은 등록뿐이다
 *   알림 권한 요청과 구독은 하지 않는다. 그건 사용자가 종 아이콘에서 "휴대폰 알림 받기" 를
 *   눌렀을 때만 해야 한다 — 페이지 로드 중에 권한 팝업을 띄우면 대부분 '차단' 을 누르고,
 *   한 번 차단된 권한은 브라우저 설정에서 직접 풀어야 해서 되돌리기가 어렵다.
 *
 * sw.js 를 우리가 고치지 않는 이유
 *   온앤미가 전달한 실기기 검증본을 그대로 쓴다. fetch 핸들러가 navigate 요청만 다루고
 *   캐시를 만들지 않아 기존 앱 동작에 개입하지 않는다. 페이로드 키(title/body/tag/url)는
 *   백엔드 PushPayloadFactory 가 그 파일에 맞춘다.
 */

const SW_URL = '/sw.js';

export function isPushCapable() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * 등록을 시도한다. 실패해도 앱 동작에 영향이 없어야 하므로 절대 throw 하지 않는다.
 *
 * `load` 이후로 미루는 이유: 등록은 네트워크와 디스크를 쓴다. 첫 화면 렌더와 경쟁시키면
 * 초기 로딩이 느려지는데, 서비스워커는 지금 당장 필요한 게 아니다(다음 방문부터 의미가 있다).
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerPushServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        // iOS 16.4 미만, 또는 네이버·카카오톡 인앱 브라우저(WebView) — 여기서 조용히 끝낸다.
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
        return registration;
    } catch (error) {
        // 가장 흔한 원인은 /sw.js 가 404 대신 index.html(200 text/html)로 응답하는 것이다.
        // 그러면 등록도 해제도 실패한다. public/ 에 두면 발생하지 않지만, 배포에서 파일이
        // 누락되면 SPA fallback 때문에 조용히 이 경로로 온다 — 그래서 원인을 문구로 남긴다.
        console.warn(
            '[push] 서비스워커 등록 실패 — 휴대폰·PC 알림이 동작하지 않습니다. ' +
            '/sw.js 의 Content-Type 이 application/javascript 인지 확인하세요.',
            error
        );
        return null;
    }
}

/** 페이지 로드가 끝난 뒤 등록. index.js 에서 한 번만 호출한다. */
export function registerPushServiceWorkerOnLoad() {
    if (!('serviceWorker' in navigator)) return;
    window.addEventListener('load', () => {
        registerPushServiceWorker();
    });
}
